<script setup lang="ts">
import PartTitleHead from '@parts/part-title-head.vue';
import PartTariffCard from '@parts/part-tariff-card.vue';
import PartPackCard from '@parts/part-pack-card.vue';
import PartPromoInput from '@parts/part-promo-input.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import PartFooterNavigation from '@parts/part-footer-navigation.vue';
import CoreLoader from '@core/core-loader.vue';
import Popup from '@components/popup/popup.vue';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import { useCurrency } from '@composables/currency';
import { usePaymentApi } from '@store/api/paymentApi';
import { usePaymentFlowStore } from '@store/paymentFlow';
import { useUserStore } from '@store/user';
import { useReserveStore } from '@store/reserve';
import type { PaymentTariff, PromoCode, ReservePack } from '@store/api/interfaces';
import router from '@/router';

const { t, locale } = useI18n();
const route = useRoute();
const toast = useToast();
const paymentApi = usePaymentApi();
const paymentFlow = usePaymentFlowStore();
const userStore = useUserStore();
const reserve = useReserveStore();
const promoInputRef = ref();
const promoPopup = ref();

const allTariffs = ref<PaymentTariff[]>([]);
const isLoading = ref(true);
const loaderText = ref(t('common.loader.provider'));

/**
 * Что оплачивает пользователь. Вкладка с пакетами появляется только у тех, кому
 * резерв уже выдан: остальным покупать нечего, и лишний переключатель на
 * странице оплаты им только мешает.
 */
type PaymentTab = 'subscription' | 'packs';
const activeTab = ref<PaymentTab>('subscription');
const selectedPackId = ref<number | null>(null);

/**
 * Было ли переключение вкладок.
 *
 * До первого переключения карточки выезжают анимацией загрузки страницы, после —
 * своей, более быстрой. Флаг не сбрасывается: класс с анимацией может висеть
 * постоянно, потому что при каждой смене вкладки карточки — новые элементы, и
 * анимация на них запускается заново сама.
 */
const tabSwitched = ref<boolean>(false);

const { getDisplayCurrency } = useCurrency();
const displayCurrency = computed(() => getDisplayCurrency(locale.value));

const filteredTariffs = computed((): PaymentTariff[] => {
	return allTariffs.value
		.filter(t => t.cost.currency === displayCurrency.value)
		.sort((a, b) => a.months - b.months);
});

// Пакеты отбираются по той же валюте, что и тарифы: показать рублёвый пакет
// рядом с долларовой подпиской значило бы обещать оплату, которой нет.
const filteredPacks = computed((): ReservePack[] => {
	return reserve.packs
		.filter(pack => pack.cost.currency === displayCurrency.value)
		.sort((a, b) => a.gb - b.gb);
});

/**
 * Вкладка с пакетами — только тем, кому сервер разрешил покупку:
 * `can_buy_pack` считается от оплаченной подписки, а не от кошелька. Если
 * купить нельзя (триал, истёкшая подписка), ни переключателя, ни пакетов нет:
 * показывать витрину, в которой нельзя ничего выбрать, незачем. Без пакетов
 * в валюте витрины вкладки тоже нет: покупать нечего.
 */
const hasPacksTab = computed((): boolean => {
	return reserve.canBuyPack && filteredPacks.value.length > 0;
});

const isPacksTab = computed((): boolean => {
	return hasPacksTab.value && activeTab.value === 'packs';
});

const selectedPack = computed((): ReservePack | undefined => {
	return filteredPacks.value.find(pack => pack.id === selectedPackId.value);
});

const selectedMonths = computed({
	get: () => paymentFlow.selectedMonths,
	set: (val) => { paymentFlow.selectedMonths = val; }
});

// One-month price for discount calculation (from server data, already includes promo if applied)
const oneMonthPrice = computed((): number => {
	const tariff = filteredTariffs.value.find(t => t.months === 1);
	if (!tariff) return 0;
	// If promo applied, use original price for discount comparison
	return tariff.cost_without_discount?.amount ?? tariff.cost.amount;
});

const bestValueMonths = computed((): number => {
	if (filteredTariffs.value.length === 0) return 0;
	return filteredTariffs.value[filteredTariffs.value.length - 1].months;
});

const canGoBack = computed((): boolean => {
	return window.history.length > 1;
});

const isPayEnabled = computed((): boolean => {
	if (isPacksTab.value) {
		return !!selectedPack.value;
	}
	return selectedMonths.value !== null;
});

const hasAppliedPromo = computed((): boolean => {
	return !!paymentFlow.appliedPromo;
});

const onSelectTariff = (months: number) => {
	selectedMonths.value = months;
};

// Unauthenticated: promo checked but not applied, re-fetch tariffs with promo_code param
const onPromoChecked = async (code: string) => {
	promoPopup.value?.open();
	try {
		paymentFlow.promoCode = code;
		allTariffs.value = await paymentApi.http.getTariffs(code);
	} finally {
		promoPopup.value?.forceClose();
	}
};

// Authenticated: promo applied via API
const onPromoApplied = async (result: PromoCode) => {
	promoPopup.value?.open();
	try {
		paymentFlow.promoCode = result.code;
		paymentFlow.appliedPromo = result;
		// Re-fetch tariffs with promo prices
		allTariffs.value = await paymentApi.http.getTariffs(result.code);
		// Refresh user so other pages see the new promo on user.promo_code
		if (userStore.loggedIn) {
			try {
				await userStore.methods.set();
			} catch (e) {
				console.error('Failed to refresh user after promo apply', e);
			}
		}
	} finally {
		promoPopup.value?.forceClose();
	}
};

const onPromoCleared = async () => {
	promoPopup.value?.open();
	try {
		const codeToCancel = paymentFlow.promoCode;
		paymentFlow.promoCode = '';
		paymentFlow.appliedPromo = null;

		// Cancel on backend if authenticated
		if (userStore.loggedIn && codeToCancel) {
			try {
				await paymentApi.http.cancelPromoCode(codeToCancel);
			} catch (e) {
				console.error('Failed to cancel promo', e);
			}
			// Refresh user so user.promo_code reflects cleared state on backend
			try {
				await userStore.methods.set();
			} catch (e) {
				console.error('Failed to refresh user after promo cancel', e);
			}
		}

		// Re-fetch tariffs without promo to reset prices
		allTariffs.value = await paymentApi.http.getTariffs();
	} finally {
		promoPopup.value?.forceClose();
	}
};

const onSelectPack = (packId: number) => {
	selectedPackId.value = packId;
};

const onSelectTab = (tab: PaymentTab) => {
	tabSwitched.value = true;
	activeTab.value = tab;
};

const goToCheckout = () => {
	if (!isPayEnabled.value) return;

	// Пакет оплачивается тем же шагом выбора платёжки, что и подписка, —
	// отличается только то, что кладётся в счёт.
	if (isPacksTab.value) {
		router.push({ name: 'PaymentCheckout', query: { pack: String(selectedPackId.value) } });
		return;
	}

	const query: Record<string, string> = {
		months: String(selectedMonths.value)
	};
	if (paymentFlow.promoCode) {
		query.promo = paymentFlow.promoCode;
	}

	router.push({ name: 'PaymentCheckout', query });
};

onBeforeMount(async () => {
	isLoading.value = true;

	// Резерв не влияет на тарифы и грузится параллельно. Без входа стор состояние
	// не запрашивает, поэтому неавторизованный посетитель просто не увидит вкладку с пакетами.
	const reserveReady = Promise.all([reserve.load(), reserve.loadPacks()]);

	// Check for promo from query param, stored value, or user profile
	const promoFromQuery = route.query.promo as string | undefined;
	const promoFromUser = userStore.loggedIn && userStore.user.promo_code?.code || undefined;
	const promoCode = promoFromQuery || paymentFlow.promoCode || promoFromUser;

	if (promoCode) {
		try {
			if (promoFromUser && promoCode === promoFromUser) {
				// Promo from user profile — already applied on backend, no need to re-apply
				paymentFlow.promoCode = promoCode;
				paymentFlow.appliedPromo = userStore.user.promo_code!;
				allTariffs.value = await paymentApi.http.getTariffs(promoCode);
				toast.success(t('payment.promo_applied'), { position: 'top' });
			} else {
				const checkResult = await paymentApi.http.checkPromoCode(promoCode);
				if (checkResult.exists) {
					if (userStore.loggedIn) {
						// Authenticated: apply promo first — the store keeps it only once
						// the backend accepted it, otherwise it would ride into the
						// checkout's ?promo= and get the invoice rejected
						const result = await paymentApi.http.applyPromoCode(promoCode);
						paymentFlow.appliedPromo = result;
					}
					paymentFlow.promoCode = promoCode;

					// Fetch tariffs with promo prices
					allTariffs.value = await paymentApi.http.getTariffs(promoCode);
					toast.success(t('payment.promo_applied'), { position: 'top' });
				} else {
					// Also covers a dead code restored from the store
					paymentFlow.clearPromo();
					toast.error(t('payment.promo_invalid'), { position: 'top' });
					allTariffs.value = await paymentApi.http.getTariffs();
				}
			}
		} catch (error) {
			// Network or rate-limit failures say nothing about the code — keep it
			const code = paymentApi.methods.promoRejectionCode(error);
			if (code) {
				paymentFlow.clearPromo();
				toast.error(t(`errorCode.${code}`), { position: 'top' });
			}
			allTariffs.value = await paymentApi.http.getTariffs();
		}
	} else {
		allTariffs.value = await paymentApi.http.getTariffs();
	}

	isLoading.value = false;

	// Auto-select best value tariff if nothing selected
	if (selectedMonths.value === null && filteredTariffs.value.length > 0) {
		selectedMonths.value = bestValueMonths.value;
	}

	await reserveReady;

	// ?tab=packs — переход со страницы резерва по кнопке «Докупить ГБ»
	if (hasPacksTab.value && route.query.tab === 'packs') {
		activeTab.value = 'packs';
	}
});
</script>

<template>
	<section class="section-payment">
		<div class="wrapper section-payment__wrapper">
			<transition appear name="fade" style="--animation-delay: 0.1s">
				<part-title-head
					:title="t('payment.title')"
					:subtitle="!isLoading ? (isPacksTab ? t('payment.packs_description') : t('payment.description')) : ''"
					class="section-payment__content"
				/>
			</transition>

			<template v-if="isLoading">
				<transition appear name="fade" style="--animation-delay: 0.3s">
					<core-loader :title="loaderText" class="section-payment__loader" />
				</transition>
			</template>

			<template v-else>
				<transition
					v-if="hasPacksTab"
					appear
					name="fade"
					style="--animation-delay: 0.2s"
				>
					<div
						class="section-payment__tabs"
						:class="{ 'section-payment__tabs_second': isPacksTab }"
					>
						<!-- Подложка активной вкладки: отдельный слой, который едет
							 между вкладками, вместо перекраски двух кнопок -->
						<span class="section-payment__tabs-pill" aria-hidden="true"></span>
						<button
							type="button"
							class="section-payment__tab"
							:class="{ 'section-payment__tab_active': !isPacksTab }"
							@click="onSelectTab('subscription')"
						>
							{{ t('payment.tab_subscription') }}
						</button>
						<button
							type="button"
							class="section-payment__tab"
							:class="{ 'section-payment__tab_active': isPacksTab }"
							@click="onSelectTab('packs')"
						>
							{{ t('payment.tab_packs') }}
						</button>
					</div>
				</transition>

				<!--
					Обе витрины живут в одном контейнере с одной анимацией появления.
					Раздельные <transition> пересоздавали список на каждом
					переключении, и вкладка полсекунды стояла пустой, пока карточки
					выезжали по очереди.
				-->
				<transition
					appear
					name="show-up-container-items"
					:style="`--animation-delay: 0.3s;
						--animation-item-delay: 0.15s;
						--animation-duration: 0.5s;
						--animation-count: ${isPacksTab ? filteredPacks.length : filteredTariffs.length}`"
				>
					<div
						class="section-payment__tariffs"
						:class="{ 'section-payment__tariffs_appear': tabSwitched }"
					>
						<template v-if="isPacksTab">
							<part-pack-card
								v-for="pack in filteredPacks"
								:key="pack.id"
								:pack="pack"
								:selected="selectedPackId === pack.id"
								@select="onSelectPack(pack.id)"
							/>
						</template>
						<template v-else>
							<part-tariff-card
								v-for="tariff in filteredTariffs"
								:key="tariff.id"
								:tariff="tariff"
								:selected="selectedMonths === tariff.months"
								:one-month-price="oneMonthPrice"
								:is-best-value="tariff.months === bestValueMonths"
								:any-selected="selectedMonths !== null"
								@select="onSelectTariff(tariff.months)"
							/>
						</template>
					</div>
				</transition>

				<!-- Условия под списком: у пакетов — как они тратятся, у подписки — гарантии -->
				<transition appear name="fade" style="--animation-delay: 0.4s">
					<p
						class="section-payment__notes"
						:class="{ 'section-payment__notes_boxed': isPacksTab }"
					>
						<template v-if="isPacksTab">
							<span>{{ t('payment.packs_note_add') }}</span>
							<span>{{ t('payment.packs_note_keep') }}</span>
							<span>{{ t('payment.packs_note_order') }}</span>
						</template>
						<template v-else>
							<span>{{ t('payment.devices_unlimited') }}</span>
							<span>{{ t('payment.guarantee') }}</span>
						</template>
					</p>
				</transition>

				<transition v-if="!isPacksTab" appear name="fade" style="--animation-delay: 0.6s">
					<div class="section-payment__promo">
						<h3 class="section-payment__promo-title">
							{{ t('payment.promo_code') }}
						</h3>
						<part-promo-input
							ref="promoInputRef"
							:require-apply="userStore.loggedIn"
							:initial-code="paymentFlow.promoCode"
							@applied="onPromoApplied"
							@checked="onPromoChecked"
							@cleared="onPromoCleared"
						/>
					</div>
				</transition>

				<transition appear name="fade" style="--animation-delay: 0.7s">
					<part-footer-navigation
						:show-back-button="canGoBack"
						class="section-payment__footer"
					>
						<button-primary
							:disabled="!isPayEnabled"
							:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
							@click="goToCheckout"
						>
							{{ t('payment.pay') }}
						</button-primary>
					</part-footer-navigation>
				</transition>
			</template>
		</div>

		<!-- Promo loading popup -->
		<popup ref="promoPopup" :closable="false">
			<core-loader class="popup__loader" />
		</popup>
	</section>
</template>

<style lang="scss">
.section-payment {
	padding: 0 0 1.6rem;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;

	&__wrapper {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
	}

	&__content {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 4.8rem;
		}
	}

	&__loader {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 6.4rem;
		}
	}

	&__tariffs {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin-bottom: 1.6rem;

		@include min-desktop {
			margin-bottom: 2rem;
		}
	}

	// Сегментированный переключатель: подложка-«дорожка» и активная таблетка,
	// как у нативных сегментов iOS — страница оплаты и так плотная, вкладки
	// не должны выглядеть ещё одним списком карточек.
	&__tabs {
		position: relative;
		display: flex;
		gap: 0.4rem;
		padding: 0.4rem;
		border-radius: 1.6rem;
		background: var(--color-panel);
		margin-bottom: 1.6rem;

		@include min-desktop {
			margin-bottom: 2rem;
		}
	}

	// Вкладки равной ширины, поэтому ширина таблетки = половина минус половина
	// зазора, а сдвиг = собственная ширина плюс зазор.
	&__tabs-pill {
		position: absolute;
		top: 0.4rem;
		bottom: 0.4rem;
		left: 0.4rem;
		width: calc(50% - 0.6rem);
		border-radius: 1.2rem;
		background: linear-gradient(135deg, #c084fc 0%, #a855f7 50%, #7c3aed 100%);
		transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
		pointer-events: none;
	}

	&__tabs_second &__tabs-pill {
		transform: translateX(calc(100% + 0.4rem));
	}

	// Появление карточек при смене вкладки: та же анимация, что при загрузке
	// страницы, но короче — переключатель должен отзываться сразу, а не
	// проигрывать вступление заново.
	&__tariffs_appear > * {
		animation-name: show-up;
		animation-duration: 0.35s;
		animation-timing-function: ease;
		animation-fill-mode: both;

		@for $i from 1 through 10 {
			&:nth-child(#{$i}) {
				animation-delay: #{($i - 1) * 0.07}s;
			}
		}
	}

	&__tab {
		position: relative;
		z-index: 1;
		flex: 1 1 0;
		min-width: 0;
		height: 4.4rem;
		padding: 0 1.2rem;
		border: none;
		border-radius: 1.2rem;
		background: transparent;
		color: var(--color-text);
		opacity: 0.6;
		cursor: pointer;
		@include font-16-pro-medium;
		transition: opacity 0.2s ease, color 0.28s ease, background-color 0.2s ease, transform 0.12s ease;

		@include min-desktop {
			height: 5.2rem;
			@include font-18-pro-medium;
		}

		&_active {
			opacity: 1;
			color: var(--color-white);
		}

		// Отклик на наведение — только там, где есть мышь. Подсветка достаётся
		// неактивной вкладке: под активной уже лежит таблетка, и фон на кнопке
		// перекрыл бы её, ведь кнопка нарисована поверх.
		@media (hover: hover) and (pointer: fine) {
			&:hover {
				opacity: 1;
			}

			&:not(.section-payment__tab_active):hover {
				background: rgba(var(--color-active-rgb), 0.08);
			}
		}

		&:active {
			transform: scale(0.98);
		}
	}

	// Обычный текст построчно, без маркеров: каждая строка — отдельный span
	&__notes {
		margin: 0 0 3.2rem;
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		@include font-12-pro-regular;
		line-height: 1.4;
		text-align: center;
		opacity: 0.5;

		@include min-desktop {
			@include font-16-pro-regular;
			margin-bottom: 4.8rem;
		}

		// Условия пакетов — плашкой: серым, как заголовки групп, строки плотно
		&_boxed {
			opacity: 1;
			gap: 0.2rem;
			padding: 1.4rem 1.6rem;
			border-radius: 1.6rem;
			background: var(--color-panel);
			color: rgba(var(--color-text-rgb), 0.5);
			@include font-regular;
			line-height: 1.35;

			@include min-desktop {
				@include font-16-pro-regular;
				line-height: 1.35;
				padding: 1.6rem 2.4rem;
			}
		}
	}

	&__promo {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 4.8rem;
		}
	}

	&__promo-title {
		@include font-24-pro-bold;
		margin-bottom: 1.6rem;

		@include max-desktop {
			font-size: 2.1rem;
		}
	}

	&__footer {
		margin-top: auto;
	}
}
</style>
